import React from "react";
import { getMeuTema } from "../services/paineisTvTemaApi";
import { TemaTokens } from "../interfaces/PaineisTvTema";
import { montarVariaveis, urlGoogleFonts, VARIAVEIS_DO_TEMA } from "./tema";

interface TemaContexto {
  tema: TemaTokens | null;
  logo: string | null;
}

const SEM_TEMA: TemaContexto = { tema: null, logo: null };
const TemaContext = React.createContext<TemaContexto>(SEM_TEMA);

export const useTema = () => React.useContext(TemaContext);

const ID_LINK_FONTE = "tema-fonte-google";

/** Carrega a fonte do Google Fonts uma única vez por URL. */
function carregarFonte(fonte: string | null) {
  const url = urlGoogleFonts(fonte);
  if (!url) return;
  let link = document.getElementById(ID_LINK_FONTE) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = ID_LINK_FONTE;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== url) link.href = url;
}

function aplicar(tokens: TemaTokens) {
  const estilo = document.documentElement.style;
  VARIAVEIS_DO_TEMA.forEach((nome) => estilo.removeProperty(nome));
  const escuro = document.documentElement.classList.contains("dark");
  Object.entries(montarVariaveis(tokens, escuro)).forEach(([nome, valor]) => estilo.setProperty(nome, valor));
}

/**
 * Busca o tema da organização uma vez e o aplica em `<html>`. Sem tema (204), sem org ou com erro,
 * nada é escrito e o visual padrão permanece. As cores de superfície só valem no claro, então o
 * tema é reaplicado quando a classe `dark` muda.
 */
export function TemaProvider({ children }: { children: React.ReactNode }) {
  const [contexto, setContexto] = React.useState<TemaContexto>(SEM_TEMA);

  React.useEffect(() => {
    let ativo = true;
    getMeuTema()
      .then((t) => {
        if (ativo && t) setContexto({ tema: t.tokens, logo: t.logo });
      })
      .catch((erro) => console.error("Não foi possível carregar o tema da organização.", erro));
    return () => {
      ativo = false;
    };
  }, []);

  React.useEffect(() => {
    const tokens = contexto.tema;
    if (!tokens) return;
    carregarFonte(tokens.fonte);
    aplicar(tokens);
    const observador = new MutationObserver(() => aplicar(tokens));
    observador.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => {
      observador.disconnect();
      VARIAVEIS_DO_TEMA.forEach((nome) => document.documentElement.style.removeProperty(nome));
    };
  }, [contexto.tema]);

  return <TemaContext.Provider value={contexto}>{children}</TemaContext.Provider>;
}
